import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ChoiceNodeView } from './ChoiceNodeView';

export interface ChoiceNodeAttributes {
	field: string;
	options: string[];
	selectedOption?: string;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		choice: {
			/**
			 * Add a choice node
			 */
			setChoice: (attributes: ChoiceNodeAttributes) => ReturnType;
		};
	}
}

export const ChoiceNode = Node.create({
	name: 'choice',

	group: 'inline',

	inline: true,

	selectable: true,

	atom: true,

	addAttributes() {
		return {
			field: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-field'),
				renderHTML: (attributes) => {
					return {
						'data-field': attributes.field,
					};
				},
			},
			options: {
				default: [],
				parseHTML: (element) => {
					const optionsAttr = element.getAttribute('data-options');
					return optionsAttr ? JSON.parse(optionsAttr) : [];
				},
				renderHTML: (attributes) => {
					return {
						'data-options': JSON.stringify(attributes.options),
					};
				},
			},
			selectedOption: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-selected-option'),
				renderHTML: (attributes) => {
					return {
						'data-selected-option': attributes.selectedOption,
					};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: 'span[data-type="choice"]',
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'choice' })];
	},

	addNodeView() {
		return ReactNodeViewRenderer(ChoiceNodeView);
	},

	addCommands() {
		return {
			setChoice:
				(attributes) =>
				({ commands }) => {
					return commands.insertContent({
						type: this.name,
						attrs: attributes,
					});
				},
		};
	},
});
